import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { useSessionStore } from "../../../Stores/SessionStore";
import { RegistrationSteps } from "../Registration";
import StepContainer from "./StepContainer";
import GradientButton from "../../Core/GradientButton";
import serviceIdExample from "../../../Images/ServiceIdExample.png";

function serviceIdText(address) {
	const raw = address?.ServiceId;
	return raw === null || raw === undefined ? "" : String(raw);
}

function getAddressLabel(address) {
	if (!address) return "";
	const segments = [address.StreetAddress];
	if (address.MDUName) segments.push(address.MDUName);
	if (address.MDUBlock) segments.push(`BLOCK ${address.MDUBlock}`);
	if (address.MDUUnitNumber) segments.push(`UNIT ${address.MDUUnitNumber}`);
	if (address.Township) segments.push(address.Township);
	return `ZNG ${address.ServiceId} | ${segments.filter(Boolean).join(", ")} | ${address.ERF ?? ""}`;
}

export default function AddressSelection(props) {
	const store = useSessionStore();

	const [query, setQuery] = useState(serviceIdText(props.address));
	const [loading, setLoading] = useState(false);
	const [results, setResults] = useState([]);
	const [selected, setSelected] = useState(props.address ?? null);
	const [showError, setShowError] = useState(false);
	const debounceRef = useRef(null);

	const search = useCallback(
		async (text) => {
			if (!text) {
				setResults([]);
				return;
			}
			setLoading(true);
			const response = await store.CustomersService.SearchAddress(text);
			setLoading(false);
			if (response?.Success && Array.isArray(response.Data)) {
				const sorted = [...response.Data].sort((a, b) => {
					const aId = serviceIdText(a);
					const bId = serviceIdText(b);
					const aExact = aId === text;
					const bExact = bId === text;
					if (aExact !== bExact) return aExact ? -1 : 1;
					const aStarts = aId.startsWith(text);
					const bStarts = bId.startsWith(text);
					if (aStarts !== bStarts) return aStarts ? -1 : 1;
					const aNum = Number(aId);
					const bNum = Number(bId);
					if (Number.isFinite(aNum) && Number.isFinite(bNum) && aNum !== bNum) {
						return aNum - bNum;
					}
					return aId.localeCompare(bId);
				});
				setResults(sorted);
			}
		},
		[store]
	);

	useEffect(() => () => {
		if (debounceRef.current) clearTimeout(debounceRef.current);
	}, []);

	function handleChange(event) {
		const next = event.target.value.replace(/\D+/g, "");
		setQuery(next);
		setSelected(null);
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => search(next), 300);
	}

	function handleSelect(item) {
		setSelected(item);
		setResults([]);
		setQuery(serviceIdText(item));
		setShowError(false);
	}

	function handleConfirm() {
		if (selected) {
			setShowError(false);
			props.onContinue(selected, RegistrationSteps.AddressSelection.Step);
		} else {
			setShowError(true);
		}
	}

	function handleNotListed() {
		setShowError(false);
		props.onContinue(null, RegistrationSteps.AddressSelection.Step);
	}

	const visibleResults = useMemo(() => (selected ? [] : results), [results, selected]);

	return (
		<StepContainer title="Type in your Service ID below and select it from the list that appears. You will find this on the bottom right of your router sticker.">
			<Row>
				<Col className="d-flex justify-content-center pb-4">
					<img src={serviceIdExample} alt="Service ID example" style={{ height: "150px" }} />
				</Col>
			</Row>
			<Row>
				<Col>
					<div
						className="d-flex align-items-center bg-white"
						style={{
							border: "1px solid #DEE2E6",
							borderRadius: 6,
							padding: "4px 12px",
						}}
					>
						<input
							className="form-control border-0"
							placeholder="Type Your Service ID"
							value={query}
							onChange={handleChange}
							inputMode="numeric"
							autoComplete="off"
							style={{ boxShadow: "none" }}
						/>
						{loading && <Spinner animation="border" size="sm" variant="success" />}
					</div>
					{visibleResults.length > 0 && (
						<ul
							className="list-unstyled bg-white mt-2 mb-0"
							style={{
								border: "1px solid #DEE2E6",
								borderRadius: 6,
								maxHeight: 260,
								overflowY: "auto",
							}}
						>
							{visibleResults.map((item) => (
								<li
									key={item.Id ?? item.ServiceId}
									onClick={() => handleSelect(item)}
									style={{
										padding: "12px 16px",
										cursor: "pointer",
										borderBottom: "1px solid #F1F3F5",
									}}
								>
									{getAddressLabel(item)}
								</li>
							))}
						</ul>
					)}
				</Col>
			</Row>
			{showError && (
				<Row className="text-center mt-3">
					<Col>
						<small className="text-danger">Please select your service ID before continuing.</small>
					</Col>
				</Row>
			)}
			<Row className="mt-4">
				<Col xs={12} className="mb-2">
					<GradientButton title="Confirm Service ID" variant="greenYellow" onClick={handleConfirm} />
				</Col>
				<Col xs={12}>
					<GradientButton title="I don't have a Service ID" variant="yellowOrange" onClick={handleNotListed} />
				</Col>
			</Row>
		</StepContainer>
	);
}
